import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Auth.css';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    acceptTerms: false,
    acceptPrivacy: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // 🆕 Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);

  // Landing page'den gelen dil bilgisini uygula
  useEffect(() => {
    const savedLanguage = localStorage.getItem('rux_language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // 🆕 Turnstile script'ini yükle
  useEffect(() => {
    // Turnstile script'ini head'e ekle
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Script yüklendiğinde Turnstile widget'ını render et
    script.onload = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAACdMhpJh8A_wA50i', // 🔑 Kendi site key'inizi buraya yazın
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileError(false);
          },
          'error-callback': () => {
            setTurnstileError(true);
            setTurnstileToken('');
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          theme: 'light', // veya 'dark'
          size: 'normal', // veya 'compact'
        });
      }
    };

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 🆕 Turnstile token kontrolü
    if (!turnstileToken) {
      setError(t('error.captchaRequired') || 'Please complete the captcha verification');
      return;
    }
    
    // Terms ve Privacy kontrolü
    if (!formData.acceptTerms) {
      setError(t('error.mustAcceptTerms'));
      return;
    }
    
    if (!formData.acceptPrivacy) {
      setError(t('error.mustAcceptPrivacy'));
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError(t('error.passwordMismatch'));
      return;
    }
    
    if (formData.password.length < 8) {
      setError(t('error.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        acceptTerms: formData.acceptTerms,
        acceptPrivacy: formData.acceptPrivacy,
        turnstileToken // 🆕 Turnstile token'ı backend'e gönder
      });
      
      if (response.data.success) {
        if (response.data.message_code) {
          setSuccess(t(`success.${response.data.message_code}`));
        } else {
          setSuccess(t('success.registrationSuccess'));
        }
        
        // Kayıt başarılı, ana sayfaya yönlendir
        setTimeout(() => {
          window.location.href = '/settings';
        }, 1500);
      } else {
        setError(t('error.registerFailed'));
        // 🆕 Başarısız kayıtta Turnstile'ı resetle
        resetTurnstile();
      }
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      setError(errorCode ? t(`error.${errorCode}`) : t('error.registerFailed'));
      // 🆕 Hata durumunda Turnstile'ı resetle
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Turnstile widget'ını resetle
  const resetTurnstile = () => {
    if (window.turnstile) {
      window.turnstile.reset();
      setTurnstileToken('');
    }
  };

  // Ana sayfaya dön butonu
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('app.name')}</h1>
          <p>{t('register.tagline')}</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fullName">{t('auth.fullName')}</label>
            <input
              id="fullName"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Ahmet Yılmaz"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="ornek@email.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('auth.passwordMinLength')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.confirmPassword')}
              required
            />
          </div>

          {/* Terms ve Privacy Checkboxes */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                required
              />
              <span>
                {t('auth.acceptTermsPrefix')}{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/terms', '_blank');
                  }}
                >
                  {t('auth.termsOfService')}
                </a>
                {' '}{t('auth.acceptTermsSuffix')}
              </span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptPrivacy"
                checked={formData.acceptPrivacy}
                onChange={handleChange}
                required
              />
              <span>
                {t('auth.acceptPrivacyPrefix')}{' '}
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/privacy', '_blank');
                  }}
                >
                  {t('auth.privacyPolicy')}
                </a>
                {' '}{t('auth.acceptPrivacySuffix')}
              </span>
            </label>
          </div>

          {/* 🆕 Cloudflare Turnstile Widget */}
          <div className="form-group cf">
            <div ref={turnstileRef}></div>
            {turnstileError && (
              <div style={{ color: '#d9534f', fontSize: '14px', marginTop: '5px' }}>
                {t('error.captchaFailed') || 'Captcha verification failed. Please try again.'}
              </div>
            )}
          </div>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <button 
            type="submit" 
            disabled={loading || !turnstileToken} 
            className="btn-primary"
            style={{
              opacity: (loading || !turnstileToken) ? 0.6 : 1,
              cursor: (loading || !turnstileToken) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? t('auth.registering') : t('auth.register')}
          </button>
        </form>
        
        <div className="auth-footer">
          {t('auth.hasAccount')}{' '}
          <Link to="/login">{t('auth.login')}</Link>
        </div>

        {/* Ana sayfaya dön linki */}
        <div className="auth-footer" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <button 
            onClick={handleBackToHome}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            ← {t('auth.backToHome') || 'Ana Sayfaya Dön'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Register;
